// Native Run C, Part 5 (docs/NATIVE_RUN_C_SPEC.md, LB-392) — sharing and file export, one way.
//
// Three places shared text with navigator.share and two downloaded a Blob through an anchor.
// Neither works inside the Capacitor WebView: navigator.share is absent there, and an
// anchor download goes nowhere. So every share and every export goes through here:
//   shareText   the native share sheet in the app (through the bridge, src/lib/nativePush.js),
//               the browser's own where it has one, the clipboard otherwise
//   exportFile  on the web, today's download; in the app, the file is written to the app's
//               cache and the share sheet is opened on it
// The web's behaviour is unchanged. scripts/check-native.mjs fails on a bare navigator.share
// or an anchor download anywhere else in src/.
import { IS_NATIVE } from "./buildFlags.js";
import { nativeBridge } from "./nativePush.js";

const dismissed = (err) => !!err && (err.name === "AbortError" || /cancel/i.test(String(err.message || "")));

// Resolves to "shared" | "copied" | "dismissed" | { error: "copy" }. `fallbackText` is what the
// clipboard gets where there is no sheet (the referral and homework shares put the bare link
// on the clipboard in copy mode, the full text in share mode).
export async function shareText(text, { fallbackText = text } = {}) {
  try {
    const b = IS_NATIVE ? nativeBridge() : null;
    if (b && typeof b.share === "function") { await b.share({ text }); return "shared"; }
    if (typeof navigator !== "undefined" && navigator.share) { await navigator.share({ text }); return "shared"; }
    await navigator.clipboard.writeText(fallbackText);
    return "copied";
  } catch (err) {
    if (dismissed(err)) return "dismissed";
    return { error: "copy" };
  }
}

// Resolves to "saved" (the web's download), "shared" (the app's sheet), "dismissed" or
// { error }. `content` is text; `mime` is the web download's type.
export async function exportFile({ name, mime = "text/plain", content }) {
  const b = IS_NATIVE ? nativeBridge() : null;
  if (b && typeof b.writeCacheFile === "function" && typeof b.share === "function") {
    try {
      const uri = await b.writeCacheFile(name, content);
      await b.share({ url: uri, title: name });
      return "shared";
    } catch (err) {
      if (dismissed(err)) return "dismissed";
      return { error: "share" };
    }
  }
  try {
    const blob = new Blob([content], { type: mime });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
    return "saved";
  } catch {
    return { error: "download" };
  }
}
